import { useMemo } from 'react';
import { useInteraction } from '@/common/hooks/useInteraction';
import type { LeaseRepresentation } from '@/common/types/interaction';
import { InteractionType } from '@/common/types/interaction';
import { useOfferExperience } from '@/views/Interactions/hooks/useOfferExperience';
import { getLocalCoverSheetConfig } from '../configurations/getLocalConfig';
import type { Card, CoverSheetConfig } from '../configurations/types';

/**
 * Drops matching fields from a card and from its layout, discarding any row
 * left empty so the remaining rows keep their intended widths.
 */
const removeFieldsFromCard = (
  card: Card,
  isExcluded: (fieldName: string) => boolean,
): Card => ({
  ...card,
  fields: card.fields.filter((field) => !isExcluded(field.name)),
  layout: card.layout
    ?.map((row) => row?.filter((item) => !isExcluded(item?.name ?? '')) ?? row)
    .filter((row) => !row || row.length > 0),
});

const setFieldsDisabled = (
  card: Card,
  isTarget: (fieldName: string) => boolean,
): Card => ({
  ...card,
  fields: card.fields.map((field) =>
    isTarget(field.name) ? { ...field, disabled: true } : field,
  ),
});

const PROPERTY_BEING_PURCHASED_CARD_ID = 'propertyInfo.propertyBeingPurchased';

// Locked on an offer's Property Info when the offer was created from a listing:
// the address is inherited from the listing and must not diverge. `mlsAutofill`
// disables the MLS vendor/number inputs and the Autofill button as a unit.
const LISTING_LOCKED_FIELD_NAMES: readonly string[] = [
  'property.street1',
  'property.street2',
  'property.city',
  'property.state',
  'property.zip',
  'property.county',
  'mlsAutofill',
];

export type CoverSheetConfigData = {
  coverSheetConfig: CoverSheetConfig | null;
};

type UseCoverSheetConfigResult = {
  data: CoverSheetConfigData | undefined;
  loading: boolean;
};

type UseCoverSheetConfigOptions = {
  interactionId: string;
  interactionType: InteractionType;
  representationType?: LeaseRepresentation | null;
  skip?: boolean;
};

/**
 * Hook that fetches the property address and builds the local cover sheet configuration
 * based on the property's state and interaction details.
 */
export function useCoverSheetConfig({
  interactionId,
  interactionType,
  representationType,
  skip,
}: UseCoverSheetConfigOptions): UseCoverSheetConfigResult {
  const { isLOMOfferExperience } = useOfferExperience({
    id: interactionId,
    type: interactionType,
  });

  const { data: interaction, isLoading: addressLoading } = useInteraction(
    interactionId,
    interactionType,
    skip,
  );

  const state = interaction?.property?.state;

  const isOfferFromListing =
    interactionType === InteractionType.offer &&
    !!interaction?.associatedListingId;

  const coverSheetData = useMemo(() => {
    if (!state) {
      // This should only ever be empty if we're loading the address or there's an error
      return undefined;
    }

    const config = getLocalCoverSheetConfig(
      state,
      interactionType,
      representationType,
    );

    let filteredConfig = config;

    // For LOM listings, the commission details card shows only Sell Side
    // (remove Buy Side = counterpartyCommission*)
    if (interactionType === InteractionType.listing && isLOMOfferExperience) {
      const BUY_SIDE_FIELD_NAMES = [
        'counterpartyCommissionType',
        'counterpartyCommissionAmountPercent',
        'counterpartyCommissionAmountDollar',
        'counterpartyCommissionAdditionalAmount',
      ] as const;
      const isBuySideField = (name: string) =>
        BUY_SIDE_FIELD_NAMES.includes(
          name as (typeof BUY_SIDE_FIELD_NAMES)[number],
        );

      filteredConfig = {
        ...filteredConfig,
        sections: filteredConfig.sections.map((section) => {
          if (section.id !== 'propertyInfo') {
            return section;
          }
          return {
            ...section,
            cards: section.cards.map((card) => {
              if (card.id !== 'propertyInfo.commissionDetails') {
                return card;
              }
              return removeFieldsFromCard(card, isBuySideField);
            }),
          };
        }),
      };
    }

    if (isOfferFromListing) {
      const isLocked = (name: string) =>
        LISTING_LOCKED_FIELD_NAMES.includes(name);
      filteredConfig = {
        ...filteredConfig,
        sections: filteredConfig.sections.map((section) =>
          section.id !== 'propertyInfo'
            ? section
            : {
                ...section,
                cards: section.cards.map((card) =>
                  card.id === PROPERTY_BEING_PURCHASED_CARD_ID
                    ? setFieldsDisabled(card, isLocked)
                    : card,
                ),
              },
        ),
      };
    }

    return {
      coverSheetConfig: filteredConfig,
    };
  }, [
    state,
    interactionType,
    representationType,
    isLOMOfferExperience,
    isOfferFromListing,
  ]);

  return {
    data: coverSheetData,
    loading: addressLoading,
  };
}
